<template>
    <div class="s-sticky-menu" :class="className">
        <div v-sticky="stickyOptions" class="sticky-bar">
            <div class="a-container">
                <div ref="submenu" class="sticky-content">
                    <div ref="submenuProduct" class="product">
                        <div v-if="submenuShown" class="product-inner">
                            <a-link
                                v-if="isSubpage && overviewLink"
                                class="title"
                                :text="overviewLink.text"
                                :to="overviewLink.to"
                            />
                            <div v-else class="title">
                                {{ navigationTitle }}
                            </div>
                            <div v-if="navigationSubtitle" class="subtitle">
                                {{ navigationSubtitle }}
                            </div>
                        </div>

                        <s-dropdown-navigation
                            v-show="!submenuShown"
                            :buttons="dropdownButtons"
                            :menu="dropdownMenu"
                            :menu-label="menuLabel"
                            :title="navigationTitle"
                            :theme="theme"
                            :event="event"
                        />
                    </div>

                    <s-product-submenu
                        v-if="submenuLinks.length && submenuShown"
                        ref="submenuLinks"
                        :submenu="submenuLinks"
                        :theme="theme"
                    />

                    <div v-if="buttons" ref="submenuButtons" class="top-buttons">
                        <a-button
                            v-for="(button, i) in renderedButtons"
                            :key="`submenu-button-${i}`"
                            :ref="i === 1 ? 'secondButton' : 'firstButton'"
                            v-modal="isDesktop && button.modal || null"
                            v-bind="button"
                            :size="button.size || 'small'"
                            :type="button.type || getButtonType(renderedButtons, i)"
                            :event="{ ...event, ...getButtonGA(button) }"
                            :class="i === 1 ? 'second' : 'first'"
                            @click="logInteraction(button)"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { debounce } from 'lodash';
import AButton from '@core/components/button/button.vue';
import ALink from '@core/components/link/link.vue';
import Modal from '@core/directives/modal.js';
import Sticky from '@core/directives/sticky.js';
import mixin from '../mixin.js';
import SDropdownNavigation from './dropdown-navigation.vue';
import SProductSubmenu from './product-submenu.vue';

const ADVANCED_PACKS_GROUP = 'advanced-packs';

export default {
    name: 'SStickyMenu',

    components: {
        AButton,
        ALink,
        SProductSubmenu,
        SDropdownNavigation,
    },

    directives: {
        Sticky,
        Modal,
    },

    mixins: [mixin],

    props: {
        navigationTitle: {
            type: String,
            required: true,
        },

        navigationSubtitle: {
            type: String,
            default: undefined,
        },

        isSubpage: {
            type: Boolean,
            default: false,
        },

        productData: {
            type: Object,
            required: true,
        },

        event: {
            type: Object,
            default: () => ({
                category: 'Conversions',
                location: 'Top CTA',
            }),
        },

        buttons: {
            type: Array,
            default: () => [],
        },

        settings: {
            type: Object,
            default: () => ({}),
        },

        theme: {
            type: String,
            validator: (prop) => ['light', 'dark'].includes(prop),
            default: 'dark',
        },
    },

    data: () => ({
        stickyOptions: { parent: '.s-sticky-menu' },
        submenuShown: true,
        submenuLinksWidth: 0,
        hideOneButton: false,
        hideTwoButtons: false,
        isLoaded: false,
        SUBMENU_ITEMS_MARGIN: 48,
    }),

    computed: {
        className() {
            return {
                'submenu-shown': this.submenuShown,
                'is-loaded': this.isLoaded,
                'light': this.theme === 'light',
            };
        },

        product() {
            return this.productData?.parameters?.common || {};
        },

        renderedButtons() {
            if (this.hideTwoButtons) return [];

            const buttons = [...this.buttons];

            if (this.hideOneButton) {
                buttons.shift();
            }

            return buttons;
        },

        dropdownButtons() {
            if (this.hideTwoButtons)  return this.buttons;

            if (this.hideOneButton) {
                return this.buttons?.length > 0 ? [this.buttons[0]] : [];
            }
            return [];
        },

        children() {
            return this.productData?.parameters?.pages?.children || [];
        },

        submenu() {
            const submenu = this.settings?.submenu || this.children;

            if (!this.isOnAdvancedPackPage) {
                return submenu;
            }

            const anchor = this.$route.fullPath.split('/').filter(Boolean).pop();

            return submenu.map((s) => (s.addAdvancedPackAnchor ? { ...s, to: `${s.to}#${anchor}` } : s));
        },

        submenuLinks() {
            return this.submenu.filter((link) => link.name !== 'overview');
        },

        isOnAdvancedPackPage() {
            const links = this.children.filter((c) => c.groupId === ADVANCED_PACKS_GROUP);

            const sublinks = links.flatMap((c) => c.sublinks);
            const submenuLinks = links
                .flatMap((c) => c.submenu).filter(Boolean)
                ?.flatMap((c) => c.columns).filter(Boolean)
                ?.flatMap((c) => c.links);

            return [...sublinks, ...submenuLinks].some((s) => this.$route.fullPath.endsWith(s?.to));
        },

        hasSubmenu() {
            return Boolean(this.submenu && this.submenu.length);
        },

        overviewLink() {
            if (!this.hasSubmenu) {
                return null;
            }
            const overviewItem = this.submenu.find((item) => item.name === 'overview');
            return overviewItem ? {
                to: overviewItem.to,
                text: this.productName,
            } : null;
        },

        overviewLabel() {
            const overviewItem = this.submenu.find((item) => item.name === 'overview');
            return overviewItem?.text;
        },

        dropdownMenu() {
            const overviewLink = this.overviewLink ? {
                ...this.overviewLink,
                text: this.overviewLabel,
            } : null;

            return [
                overviewLink,
                ...this.submenu.filter((item) => item.name !== 'overview'),
            ].filter(Boolean);
        },

        menuLabel() {
            return this.product.menuLabel || 'Menu';
        },
    },

    mounted() {
        if (!this.submenu.length) {
            this.productBarLoaded = true;
        }

        if (document.readyState === 'complete') {
            this.load();
        } else {
            window.addEventListener('load', this.load);
        }

        document.addEventListener('click', this.hideDropdown);
        window.addEventListener('scroll', this.calculate);

        this.calculateDebounced = debounce(this.calculate, 1000);
        window.addEventListener('resize', this.calculateDebounced);
    },

    beforeDestroy() {
        if (!this.submenu.length) {
            return;
        }
        window.removeEventListener('load', this.load);
        window.removeEventListener('scroll', this.calculate);
        window.removeEventListener('resize', this.calculateDebounced);
    },

    methods: {
        async load() {
            const { submenuLinks } = this.$refs;

            this.submenuLinksWidth = submenuLinks?.$el.offsetWidth || 0;

            await this.calculate();

            this.isLoaded = true;
        },

        async calculate() {
            this.submenuShown = true;
            this.hideOneButton = false;
            this.hideTwoButtons = false;

            await this.$nextTick();

            // if menu doesn’t fit - remove button 1 to keep menu
            if (this.renderedButtons.length > 1) {
                this.hideOneButton = this.isWidthOverflown();
                if (this.isWidthOverflown() && !this.submenu.length) {
                    this.submenuShown = false;
                }
                await this.$nextTick();
            }

            if (this.isWidthOverflown()) {
                this.submenuShown = false;
                this.hideOneButton = false;
                await this.$nextTick();
            }

            if (this.isWidthOverflown() && !this.submenuShown) {
                this.hideOneButton = true;
                await this.$nextTick();
            }

            if (this.isWidthOverflown() && this.hideOneButton) {
                this.hideTwoButtons = true;
                await this.$nextTick();
            }

            // if (!this.buttons.length > 1) {
            //     this.showDropdownMenu = this.isWidthOverflown();
            // }
        },

        isWidthOverflown() {
            const { submenuProduct, submenu, submenuButtons } = this.$refs;

            const availableWidth = (submenu?.offsetWidth || 0) - this.SUBMENU_ITEMS_MARGIN;

            const submenuProductWidth = submenuProduct?.offsetWidth || 0;
            const submenuButtonsWidth = submenuButtons?.offsetWidth || 0;
            const linksWidth = !this.submenuShown ? 0 : (this.submenuLinksWidth || 0);

            return submenuProductWidth + linksWidth + submenuButtonsWidth >= availableWidth;
        },
    },
};
</script>

<style lang="postcss" scoped>
.s-sticky-menu {
    visibility: hidden;
    opacity: 0;
    width: 100%;
    height: 57px;
    margin-bottom: 40px;

    /* should be over anchor-menu when dropdown is opened */
    z-index: 121;

    &.is-loaded {
        visibility: visible;
        opacity: 1;
    }

    @media (--viewport-mobile-wide) {
        height: 64px;
    }

    @media (--viewport-desktop-wide) {
        height: 65px;
    }

    &:not(.submenu-shown) .sticky-bar {
        height: 58px;

        &:after {
            position: absolute;
            content: '';
            z-index: 10;
            height: 1px;
            inset-inline-start: 0;
            inset-inline-end: 0;
            bottom: -2px;
            background: rgba(0, 101, 227, 0.4);
        }

        @media (--viewport-mobile-wide) {
            height: 66px;
        }

        &.two-buttons-hidden {
            .product {
                width: 100%;
            }

            .toggler {
                padding: 0;
            }
        }
    }

    &.light {
        .sticky-content {
            .title {
                color: var(--av-nav-primary);
            }
            .subtitle {
                background: linear-gradient(90deg, #519eff 0%, #0071ff 50%, #005bcc 100%);
                background-clip: text;
            }
        }

        &:not(.submenu-shown) .sticky-bar:after {
            background-color: var(--av-brand-secondary-light);
        }


        .sticky-bar {
            border-color: var(--av-brand-secondary-light);


            &:not(.is-sticky) {
                .a-button {
                    border-color: var(--av-button-secondary);
                    color: var(--av-link-primary);

                    &:hover {
                        color: var(--av-inversed-primary);
                    }
                }
            }

            &.is-sticky {
                background: var(--av-solid-brand-lightest);
                color: var(--av-nav-primary);
                border: 0;
            }
        }
    }
}

.sticky-bar {
    top: 0;
    border-top: 1px solid rgba(0, 101, 227, 0.4);
    height: 57px;
    padding-top: 8px;
    position: relative;

    @media (--viewport-mobile-wide) {
        height: 65px;
        padding-top: 12px;
    }

    &.two-buttons-hidden {
        .product {
            width: 100%;
        }

        .toggler {
            padding: 0;
        }
    }

    &.is-sticky {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        width: 100%;
        box-shadow: 0 10px 20px rgba(36, 49, 67, 0.2);
        border-top: 0;
        background: var(--av-nav-primary);
        border-bottom: 1px solid rgba(0, 101, 227, 0.4);
        z-index: 1000;

        &::after {
            display: none;
        }

        .subtitle {
            display: none;
        }
    }

    .a-button {
        + .a-button {
            margin-inline-start: 16px;
        }
    }
}

.sticky-bar:not(.is-sticky) {
    .a-button {
        background: rgba(255, 255, 255, 0);
        border: 1px solid var(--av-brand-secondary);
        padding: 7px 15px;
        transition: background 0.15s ease, border 0.15s ease;

        &.a-button_type_main {
            &:hover {
                background: var(--av-nav-secondary);
                border-color: var(--av-nav-secondary);
            }

            &:active {
                background: #0058b2;
                border-color: #0058b2;
            }
        }

        &.a-button_type_action {
            &:hover {
                background: var(--av-fixed-success);
                border-color: var(--av-fixed-success);
            }

            &:active {
                background: var(--av-fixed-success-dark);
                border-color: var(--av-fixed-success-dark);
            }
        }
    }
}

.sticky-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 40px;

    .product {
        white-space: nowrap;
    }

    .title {
        @mixin nav-title-accent;
        font-weight:400;
        color: var(--av-inversed-primary);
        text-decoration: none;
        margin-top: -2px;
        margin-bottom: 4px;

        &:deep(.a-link__content) {
            @mixin nav-title-accent;
            font-weight:400;
            color: var(--av-inversed-primary);
        }

        &:hover,
        &:focus {
            &:deep(.a-link__content) {
                background: none;
                color: var(--av-inversed-light);
            }
        }
    }

    .subtitle {
        position: absolute;
        font-size: 10px;
        line-height: 16px;
        letter-spacing: 4px;
        text-transform: uppercase;
        padding-inline-end: 48px;
        font-weight: 600;
        color: transparent;
        background: linear-gradient(90deg, #cde2ff 0%, #7db7ff 100%);
        background-clip: text;
    }

    .top-buttons {
        margin-inline-start: auto;
        white-space: nowrap;
        display: flex;
        align-items: center;
    }
}
</style>
